import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { lastValueFrom, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { CompletedWebsiteReport, discoverLatestWebsiteReport, ReportCandidate } from './latest-website-report';
import { discoverWebsiteScans, WebsiteScanItem } from './website-scan-list';
import { LegacyWebsiteReport, summarizeLegacyWebsiteReport, WebsiteReportSummary } from './website-report-summary';

@Injectable({ providedIn: 'root' })
export class LatestWebsiteReportService {
  private http = inject(HttpClient);

  private async page(index: number, organizationId: string, search = '') {
    const params = new HttpParams().set('pageIndex', index).set('pageSize', 50)
      .set('activeSort', 'scanDate').set('sortDirection', 'desc').set('search', search)
      .set('shareStatus', 'all').set('organizationId', organizationId);
    const response = await lastValueFrom(this.http.get<ReportCandidate[]>(`${environment.cfBackend}/projects`,
      { params, observe: 'response' }).pipe(timeout(15000)));
    const count = response.headers.get('x-total-count');
    return { projects: response.body ?? [], count: count === null ? NaN : Number(count) };
  }

  list(userId: string, organizationId: string, excludeId?: string) {
    return discoverWebsiteScans({userId, organizationId, excludeId, page: i => this.page(i, organizationId)});
  }

  async readSelection(item: WebsiteScanItem, userId: string, organizationId: string) {
    // Recheck dashboard access rather than trusting a historical card or client-supplied ID.
    let project: ReportCandidate | undefined;
    for (let i=0;i<4;i++) {
      const page=await this.page(i, organizationId, item.name);
      project=page.projects.find(p=>p.id===item.id && p.type==='website' && (p.userId===userId||p.shared===true));
      if(project) break;
      if(!page.projects.length || (i+1)*50>=page.count) break;
    }
    if(!project) throw Error('This scan is no longer available in the current organization. Refresh the scan list.');
    let report: CompletedWebsiteReport;
    try {
      report=await lastValueFrom(this.http.get<CompletedWebsiteReport>(
        `${environment.cfBackend}/reports-v2/website/${encodeURIComponent(project.id)}`).pipe(timeout(15000)));
    } catch(error) {
      // Legacy reports live in the original authenticated API. Never fall back on an auth denial.
      if(!(error instanceof HttpErrorResponse)||![400,404,500].includes(error.status))throw error;
      const legacy=await lastValueFrom(this.http.get<LegacyWebsiteReport>(
        `${environment.baseUrl}/users/${encodeURIComponent(userId)}/scan-projects/${encodeURIComponent(project.id)}/website/reports`).pipe(timeout(15000)));
      return {project,summary:summarizeLegacyWebsiteReport(legacy,project.id,project.name)};
    }
    if(report.scanProjectId!==project.id||report.scanType!=='website') throw Error('Could not verify this website scan.');
    if(report.status!=='SCANNED'||report.canceled!==false) throw Error('This scan does not have a completed report to review yet. Open the report to check its status.');
    return {project,summary:undefined as WebsiteReportSummary|undefined};
  }

  find(userId: string, organizationId: string) {
    return discoverLatestWebsiteReport({
      userId,
      page: async index => {
        const params = new HttpParams().set('pageIndex', index).set('pageSize', 50)
          .set('activeSort', 'scanDate').set('sortDirection', 'desc').set('search', '')
          .set('shareStatus', 'all').set('organizationId', organizationId);
        const response = await lastValueFrom(this.http.get<ReportCandidate[]>(`${environment.cfBackend}/projects`,
          { params, observe: 'response' }).pipe(timeout(15000)));
        const count = response.headers.get('x-total-count');
        return { projects: response.body ?? [], count: count === null ? NaN : Number(count) };
      },
      report: id => lastValueFrom(this.http.get<CompletedWebsiteReport>(
        `${environment.cfBackend}/reports-v2/website/${encodeURIComponent(id)}`).pipe(timeout(15000))),
    });
  }
}
